import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { LocationRow } from "@garden/types/db";
import { supabase } from "../auth";
import { listLocations } from "../db/locations";
import { createPlant } from "../db/plants";
import { useCurrentGarden } from "../lib/useCurrentGarden";
import { PlantForm } from "../components/PlantForm";
import type { PlantCreateInput } from "../db/schemas";
import "./PlantPage.css";

export function PlantNew() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  // Set when arriving from /tag's "Add new plant" branch — we hand the
  // photo id forward so /tag can attach it once the plant exists.
  const tagPhotoState = (routerLocation.state as
    | { fromTagPhotoId?: string; returnTo?: string }
    | null) ?? null;
  const fromTagPhotoId = tagPhotoState?.fromTagPhotoId ?? null;
  const returnTo = tagPhotoState?.returnTo ?? null;
  const { current: garden, loading: gardenLoading } = useCurrentGarden();
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase || !garden) return;
    let active = true;
    (async () => {
      try {
        const locs = await listLocations(supabase!, garden.id);
        if (!active) return;
        setLocations(locs);
      } catch (err) {
        if (!active) return;
        setBootstrapError(err instanceof Error ? err.message : "Could not load locations.");
      }
    })();
    return () => {
      active = false;
    };
  }, [garden?.id]);

  if (bootstrapError) {
    return <div className="PlantPage__error">{bootstrapError}</div>;
  }
  if (gardenLoading || !garden) {
    return <div className="PlantPage__loading">Preparing your garden…</div>;
  }

  const handleSubmit = async (input: PlantCreateInput) => {
    if (!supabase) throw new Error("Supabase not configured.");
    // Plants without a location need an explicit garden_id (the trigger
    // can't derive one). Plants with a location: the trigger derives,
    // but we also pass it for symmetry.
    const plant = await createPlant(supabase, { ...input, garden_id: garden.id });
    if (returnTo === "/tag" && fromTagPhotoId) {
      // Hand the photo id and new plant id back to /tag so it can attach
      // the photo_tag and advance the queue.
      navigate("/tag", {
        state: { taggedPhotoId: fromTagPhotoId, createdPlantId: plant.id },
        replace: true,
      });
      return;
    }
    navigate(`/plants/${plant.id}`);
  };

  const cancel = () => {
    if (returnTo === "/tag") {
      navigate("/tag", { replace: true });
    } else {
      navigate("/plants");
    }
  };

  return (
    <div className="PlantPage">
      <div className="PlantPage__crumbs">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            cancel();
          }}
        >
          {returnTo === "/tag" ? "Tag" : "Plants"}
        </a>
        <span>›</span>
        <em>Add</em>
      </div>
      <h1 className="PlantPage__title">
        {fromTagPhotoId ? "Add a plant for this photo" : "Add a plant"}
      </h1>
      {fromTagPhotoId && (
        <p className="PlantPage__hint">
          Save the plant and this photo will be tagged to it automatically.
        </p>
      )}
      <PlantForm
        locations={locations}
        gardenId={garden.id}
        submitLabel={fromTagPhotoId ? "Save & tag photo" : "Save plant"}
        onSubmit={handleSubmit}
        onCancel={cancel}
        onLocationCreated={(loc) => setLocations((ls) => [loc, ...ls])}
      />
    </div>
  );
}
